/**
 * Field types and their registry.
 *
 * A `FieldType` has one of three `kind`s, each a storage strategy:
 *
 *   - value:     `record.data[key]` holds the raw value (string, number, boolean,
 *                timestamp, select, object).
 *   - reference: a row id (or ids) resolving into `table`, by a by-id lookup or a
 *                `relationship` on the record (`record.contacts`, …). A junction
 *                (link/tagging/attachment) owns a row in `table` whose inner
 *                `reference` points at the real entity; that inner `reference` is
 *                what marks it a junction.
 *   - computed:  derived at read time by following a `path` across a link (lookup).
 *                Not persisted.
 *
 * Every entry is flat: a type spreads its parent at definition, so `markdown`
 * carries `string`'s props and `email` carries `contact`'s. `parent` names that
 * parent for the operator registry. The helpers that operate on a `Field` are in
 * `./fields.ts`; operators are in `./operators/index.ts`.
 */

import type { AssetView } from "./assets.js"
import type { AllGroups, MatchesGroup } from "./group.js"
import type { Compare } from "./sort.js"
import type { ComputedType } from "./types/computed.js"
import { fieldTypes as computedDefs } from "./types/computed.js"
import type { AttachmentType, JunctionType } from "./types/junction.js"
import { fieldTypes as junctionDefs } from "./types/junction.js"
import type { ContactType, RecordType, ReferenceType, TimeType } from "./types/reference.js"
import { fieldTypes as referenceDefs } from "./types/reference.js"
import type { NumberType, ObjectType, SelectType, StringType, ValueType } from "./types/value.js"
import { fieldTypes as valueDefs } from "./types/value.js"

// PUBLIC TYPES ================================================================

/** The three root types, one per storage strategy. */
export type RootType = "value" | "reference" | "computed"

// Each def file owns its type-name unions; they are re-exported here.
export type {
  ComputedType,
  AttachmentType,
  JunctionType,
  ContactType,
  ReferenceType,
  RecordType,
  TimeType,
  NumberType,
  ObjectType,
  SelectType,
  StringType,
  ValueType,
}

/** Every concrete field-type name, top-level and subtype. A field stores its type
 *  directly (`type: "email"`); `recordType` (see `Field`) scopes a
 *  link/record/type field to a record type. */
export type FieldTypeName =
  | ValueType
  | ReferenceType
  | JunctionType
  | ComputedType
  | StringType
  | NumberType
  | SelectType
  | ObjectType
  | TimeType
  | ContactType
  | RecordType
  | AttachmentType

/** Props common to every kind. */
export interface BaseFieldType {
  /** The storage root: the discriminant of the `FieldType` union. */
  kind: RootType
  name: RootType | FieldTypeName
  label: string
  /** FontAwesome icon name. */
  icon: string
  /** The type this one extends; absent on the roots. */
  parent?: RootType | FieldTypeName
  /** How two values of this type order. A type with a `sort` is sortable. */
  sort?: Compare
  isEditable?: boolean
  /** The buckets a grouped collection shows for this type. A type with `allGroups` and
   *  `matchesGroup` is groupable. */
  allGroups?: AllGroups
  /** Whether a value falls in a bucket. */
  matchesGroup?: MatchesGroup
  /** Props a field of this type must declare: `select` → `values`, `lookup` → `path`. */
  requiredProps?: readonly string[]
  /** A value as display text. */
  format?: (value: unknown) => string
  /** Raw input as a storable value; `null` when non-empty input is malformed. */
  parse?: (input: unknown) => unknown
}

/** A value type: `record.data[key]` holds the raw value. */
export interface ValueFieldType extends BaseFieldType {
  /** The options of a fixed-vocabulary type (select subtypes). */
  values?: readonly unknown[]
}

/** A reference type: a row id (or ids) resolving into `table`. A junction owns
 *  that row and carries an inner `reference` to the real entity. */
export interface ReferenceFieldType extends BaseFieldType {
  /** The table the stored id resolves into; `records` by default. */
  table: string
  /** The relation to read off the record: target rows (`contacts`) or owned junction rows (`taggings`). */
  relationship?: string
  /** A junction's hop from its owned row to the real entity (`linked` on
   *  record_links → records). Its presence marks a junction. */
  reference?: ReferenceFieldType
  /** The FK column on a junction's owned row holding the target's id (`linked_id`
   *  on record_links), read as the cell's target id even when the hop is unresolved. */
  column?: string
  /** The record types a link/record/type field can scope to. */
  recordTypes?: () => string[]
  /** The kind of file an attachment subtype holds; writes of another kind are refused. */
  accepts?: AssetView
}

/** A computed type: derived at read time by following a `path` across a link. */
export type ComputedFieldType = BaseFieldType

/** The shape each `types/<name>.ts` exports: a union over the three kinds,
 *  discriminated by `kind`. */
export type FieldType = ValueFieldType | ReferenceFieldType | ComputedFieldType

// REGISTRY ====================================================================

/** Every registered FieldType. */
export const DEFS: readonly FieldType[] = [
  ...Object.values(valueDefs),
  ...Object.values(referenceDefs),
  ...Object.values(junctionDefs),
  ...Object.values(computedDefs),
]

export const FIELD_TYPES: Record<string, FieldType> = Object.fromEntries(
  DEFS.map((d) => [d.name, d])
)
